/**
 * Editable Data Table Component with Full CRUD operations
 * 
 * Features:
 * - Add new rows
 * - Edit cells inline
 * - Delete rows
 * - Generic: works with any row type
 */

import React, { useState } from "react";
import { Table, Button, Form } from "react-bootstrap";
import "./style.css";

export interface EditableTableColumn<T> {
  key: keyof T;
  label: string;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
  width?: string;
  minWidth?: string;
  render?: (value: any, rowIndex: number, onChange: (value: any) => void, isReadonly: boolean) => React.ReactNode;
}

interface EditableTableProps<T> {
  rows: T[];
  columns: EditableTableColumn<T>[];
  onChange: (rows: T[]) => void;
  createEmptyRow: () => T;
  isReadonly?: boolean;
  label?: string;
  required?: boolean;
  scrollable?: boolean;
}

function EditableTable<T extends Record<string, any>>({
  rows,
  columns,
  onChange,
  createEmptyRow,
  isReadonly = false,
  label,
  required = false,
  scrollable = false,
}: EditableTableProps<T>) {
  const [localRows, setLocalRows] = useState<T[]>(rows);

  // Sync local state when props change
  React.useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const handleCellChange = (rowIndex: number, field: keyof T, value: string) => {
    const updatedRows = [...localRows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]: value,
    };
    setLocalRows(updatedRows);
    onChange(updatedRows);
  };

  const handleAddRow = () => {
    const newRow = createEmptyRow();
    const updatedRows = [...localRows, newRow];
    setLocalRows(updatedRows);
    onChange(updatedRows);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedRows = localRows.filter((_, index) => index !== rowIndex);
    setLocalRows(updatedRows);
    onChange(updatedRows);
  };

  return (
    <div className="editable-table-container mb-3">
      {label && (
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      
      <div className={`table-responsive${scrollable ? ' table-scrollable' : ''}`}>
        <Table bordered hover size="sm" className="editable-table">
          <thead>
            <tr>
              <th style={{ width: "40px" }}>#</th>
              {columns.map((col) => (
                <th key={String(col.key)} style={{ ...(col.width ? { width: col.width } : {}), ...(col.minWidth ? { minWidth: col.minWidth } : {}), whiteSpace: 'nowrap' }}>
                  {col.label} {col.required && <span className="text-danger">*</span>}
                </th>
              ))}
              {!isReadonly && <th style={{ width: "80px" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {localRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (isReadonly ? 1 : 2)} className="text-center text-muted">
                  No data. Click "Add Row" to begin.
                </td>
              </tr>
            ) : (
              localRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="text-center">{rowIndex + 1}</td>
                  {columns.map((col) => (
                    <td key={String(col.key)} title={col.placeholder} style={{ ...(col.width ? { width: col.width } : {}), ...(col.minWidth ? { minWidth: col.minWidth } : {}) }}>
                      {col.render ? (
                        col.render(
                          row[col.key],
                          rowIndex,
                          (newValue) => handleCellChange(rowIndex, col.key, newValue),
                          isReadonly
                        )
                      ) : (
                        <Form.Control
                          type="text"
                          value={String(row[col.key] || "")}
                          onChange={(e) => handleCellChange(rowIndex, col.key, e.target.value)}
                          maxLength={col.maxLength}
                          placeholder={col.placeholder}
                          readOnly={isReadonly}
                          className={isReadonly ? "readonly" : ""}
                          size="sm"
                        />
                      )}
                    </td>
                  ))}
                  {!isReadonly && (
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteRow(rowIndex)}
                        title="Delete row"
                      >
                        <i className="bi bi-trash"></i> Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {!isReadonly && (
        <Button variant="outline-primary" size="sm" onClick={handleAddRow}>
          <i className="bi bi-plus-circle"></i> Add Row
        </Button>
      )}
    </div>
  );
}

export default EditableTable;
